import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Lock, 
  Image as ImageIcon, 
  Check, 
  RotateCcw, 
  Eye, 
  Layout, 
  User, 
  Briefcase, 
  Grid, 
  LogOut,
  AlertCircle,
  ExternalLink,
  Globe,
  RefreshCw,
  Copy,
  Sliders
} from 'lucide-react';
import { useCustomImages, DEFAULT_IMAGES } from '@/context/ImageContext';
import { getStoredFirebaseConfig, saveStoredFirebaseConfig } from '@/lib/firebaseSync';

const ADMIN_SECRET = '1983';

export const AdminImageModal = () => {
  const { 
    isAdminOpen, 
    closeAdminPortal, 
    isAdminAuthenticated, 
    setIsAdminAuthenticated,
    getImage,
    setImage,
    resetImage,
    resetAllImages,
    forceCloudSync,
    syncStatus,
    lastSyncedTime,
    customImages
  } = useCustomImages();

  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('branding');
  const [copiedKey, setCopiedKey] = useState(null);
  const [tempInputs, setTempInputs] = useState({});
  const [isSyncingNow, setIsSyncingNow] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);

  // Custom Firebase Settings in Modal
  const [customFirebaseJson, setCustomFirebaseJson] = useState(() => {
    const cfg = getStoredFirebaseConfig();
    return cfg ? JSON.stringify(cfg, null, 2) : '';
  });
  const [firebaseSavedSuccess, setFirebaseSavedSuccess] = useState(false);

  if (!isAdminOpen) return null;

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_SECRET) {
      setIsAdminAuthenticated(true);
      setAuthError('');
      setPasswordInput('');
    } else {
      setAuthError('Incorrect admin passcode. Access denied.');
    }
  };

  const handleLogout = () => {
    setIsAdminAuthenticated(false);
    setPasswordInput('');
  };

  const handleApplyImage = async (key, url) => {
    setCopiedKey(key);
    await setImage(key, url);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleTempInputChange = (key, value) => {
    setTempInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleManualSync = async () => {
    setIsSyncingNow(true);
    await forceCloudSync();
    setTimeout(() => setIsSyncingNow(false), 800);
  };

  const handleCopyJson = () => {
    const combined = { ...DEFAULT_IMAGES, ...customImages };
    navigator.clipboard.writeText(JSON.stringify(combined, null, 2));
    setJsonCopied(true);
    setTimeout(() => setJsonCopied(false), 2000);
  };

  const handleSaveFirebaseConfig = (e) => {
    e.preventDefault();
    try {
      if (!customFirebaseJson.trim()) {
        saveStoredFirebaseConfig(null);
        setFirebaseSavedSuccess(true);
        setTimeout(() => setFirebaseSavedSuccess(false), 2500);
        return;
      }
      const parsed = JSON.parse(customFirebaseJson);
      saveStoredFirebaseConfig(parsed);
      setFirebaseSavedSuccess(true);
      setTimeout(() => setFirebaseSavedSuccess(false), 2500);
    } catch (err) {
      alert('Invalid JSON format for Firebase configuration. Please check the syntax.');
    }
  };

  // Image field configuration by page tab
  const imageConfig = {
    branding: [
      {
        key: 'headerLogo',
        title: 'Header Navigation Logo',
        pageName: 'All Pages - Top Header',
        defaultUrl: DEFAULT_IMAGES.headerLogo,
        description: 'Main high-resolution logo displayed in top navbar across all devices'
      },
      {
        key: 'footerLogo',
        title: 'Footer Brand Logo',
        pageName: 'All Pages - Footer',
        defaultUrl: DEFAULT_IMAGES.footerLogo,
        description: 'Brand logo displayed at the bottom footer section'
      }
    ],
    home: [
      {
        key: 'homeHeroBg',
        title: 'Hero Background Banner',
        pageName: 'Home Page',
        defaultUrl: DEFAULT_IMAGES.homeHeroBg,
        description: 'Main high-impact hero image background for Stellars Estates'
      }
    ],
    founder: [
      {
        key: 'founderPhoto',
        title: 'Sahil Sharma Profile Photo',
        pageName: 'Founder Page',
        defaultUrl: DEFAULT_IMAGES.founderPhoto,
        description: 'Main hero portrait image of Founder & CEO Sahil Sharma'
      }
    ],
    services: [
      {
        key: '/services/advertisement',
        title: 'Advertisement Category Banner',
        pageName: 'Services Page - Advertisement',
        defaultUrl: DEFAULT_IMAGES['/services/advertisement'],
        description: 'Hero cover image for Advertisement category'
      },
      {
        key: '/services/marketing',
        title: 'Marketing Category Banner',
        pageName: 'Services Page - Marketing',
        defaultUrl: DEFAULT_IMAGES['/services/marketing'],
        description: 'Hero cover image for Marketing category'
      },
      {
        key: '/services/development',
        title: 'Development Category Banner',
        pageName: 'Services Page - Development',
        defaultUrl: DEFAULT_IMAGES['/services/development'],
        description: 'Hero cover image for Development category'
      },
      {
        key: '/services/search-engine-optimisation',
        title: 'SEO & Search Optimisation Banner',
        pageName: 'Services Page - SEO',
        defaultUrl: DEFAULT_IMAGES['/services/search-engine-optimisation'],
        description: 'Hero cover image for Search Engine Optimisation'
      },
      {
        key: '/services/ai-automation',
        title: 'AI Automation Banner',
        pageName: 'Services Page - AI Automation',
        defaultUrl: DEFAULT_IMAGES['/services/ai-automation'],
        description: 'Hero cover image for AI & Bot Workflows'
      },
      {
        key: '/services/designing-editing',
        title: 'Designing & Editing Banner',
        pageName: 'Services Page - Designing & Editing',
        defaultUrl: DEFAULT_IMAGES['/services/designing-editing'],
        description: 'Hero cover image for Visual Branding & Design'
      },
      {
        key: '/services/integration',
        title: 'API & Integration Banner',
        pageName: 'Services Page - Integration',
        defaultUrl: DEFAULT_IMAGES['/services/integration'],
        description: 'Hero cover image for Technical Integrations'
      },
      {
        key: '/services/consultations',
        title: 'Consultations Category Banner',
        pageName: 'Services Page - Consultations',
        defaultUrl: DEFAULT_IMAGES['/services/consultations'],
        description: 'Hero cover image for Strategic Advisory'
      },
      {
        key: '/services/on-site-dedicated-services',
        title: 'On-Site & Dedicated Services Banner',
        pageName: 'Services Page - Dedicated Support',
        defaultUrl: DEFAULT_IMAGES['/services/on-site-dedicated-services'],
        description: 'Hero cover image for In-Person Operational Support'
      }
    ],
    subservices: [
      {
        key: '/services/advertisement/online/google-ads',
        title: 'Google Ads & PPC Hero Image',
        pageName: 'Service Detail - Google Ads',
        defaultUrl: DEFAULT_IMAGES['/services/advertisement/online/google-ads'],
        description: 'Hero image for Google Ads performance service'
      },
      {
        key: '/services/advertisement/online/meta-ads',
        title: 'Meta Ads (Facebook & Instagram) Hero Image',
        pageName: 'Service Detail - Meta Ads',
        defaultUrl: DEFAULT_IMAGES['/services/advertisement/online/meta-ads'],
        description: 'Hero image for Meta Facebook & Instagram Ads service'
      },
      {
        key: '/services/advertisement/online/ott-ads',
        title: 'OTT & Streaming Ads Hero Image',
        pageName: 'Service Detail - OTT Ads',
        defaultUrl: DEFAULT_IMAGES['/services/advertisement/online/ott-ads'],
        description: 'Hero image for Hotstar, JioCinema & SonyLIV ads'
      },
      {
        key: '/services/advertisement/offline/pvr-inox',
        title: 'PVR / INOX Cinema Ads Hero Image',
        pageName: 'Service Detail - Cinema Ads',
        defaultUrl: DEFAULT_IMAGES['/services/advertisement/offline/pvr-inox'],
        description: 'Hero image for Cinema & On-screen advertising'
      },
      {
        key: '/services/marketing/social-media-marketing',
        title: 'Social Media Marketing Hero Image',
        pageName: 'Service Detail - Social Media',
        defaultUrl: DEFAULT_IMAGES['/services/marketing/social-media-marketing'],
        description: 'Hero image for Social Media Management & Organic Growth'
      },
      {
        key: '/services/marketing/whatsapp-marketing',
        title: 'WhatsApp Marketing Hero Image',
        pageName: 'Service Detail - WhatsApp Marketing',
        defaultUrl: DEFAULT_IMAGES['/services/marketing/whatsapp-marketing'],
        description: 'Hero image for WhatsApp Broadcasts & Funnels'
      },
      {
        key: '/services/development/websites',
        title: 'Websites & Web Apps Hero Image',
        pageName: 'Service Detail - Websites',
        defaultUrl: DEFAULT_IMAGES['/services/development/websites'],
        description: 'Hero image for Custom Web Development'
      },
      {
        key: '/services/development/e-commerce',
        title: 'E-Commerce Stores Hero Image',
        pageName: 'Service Detail - E-Commerce',
        defaultUrl: DEFAULT_IMAGES['/services/development/e-commerce'],
        description: 'Hero image for Online Stores & Shopping Funnels'
      },
      {
        key: '/services/development/mobile-apps',
        title: 'Mobile Apps Hero Image',
        pageName: 'Service Detail - Mobile Apps',
        defaultUrl: DEFAULT_IMAGES['/services/development/mobile-apps'],
        description: 'Hero image for iOS & Android Mobile Apps'
      },
      {
        key: '/services/search-engine-optimisation/seo',
        title: 'Search Engine Optimisation (SEO) Hero Image',
        pageName: 'Service Detail - SEO',
        defaultUrl: DEFAULT_IMAGES['/services/search-engine-optimisation/seo'],
        description: 'Hero image for Organic Search Optimisation'
      },
      {
        key: '/services/ai-automation/whatsapp-ai-automation',
        title: 'WhatsApp AI Automation Hero Image',
        pageName: 'Service Detail - WhatsApp AI',
        defaultUrl: DEFAULT_IMAGES['/services/ai-automation/whatsapp-ai-automation'],
        description: 'Hero image for Smart AI Conversational Agents'
      },
      {
        key: '/services/designing-editing/creative-designing',
        title: 'Creative Designing Hero Image',
        pageName: 'Service Detail - Creative Design',
        defaultUrl: DEFAULT_IMAGES['/services/designing-editing/creative-designing'],
        description: 'Hero image for Brand Visuals & Ad Creatives'
      },
      {
        key: '/services/integration/ai-integration',
        title: 'AI & API Integration Hero Image',
        pageName: 'Service Detail - Integrations',
        defaultUrl: DEFAULT_IMAGES['/services/integration/ai-integration'],
        description: 'Hero image for System & API Integrations'
      },
      {
        key: '/services/consultations/strategy',
        title: 'Growth Strategy Consultation Hero Image',
        pageName: 'Service Detail - Strategy',
        defaultUrl: DEFAULT_IMAGES['/services/consultations/strategy'],
        description: 'Hero image for Strategic Advisory'
      },
      {
        key: '/services/on-site-dedicated-services/in-person-support',
        title: 'In-Person Support Hero Image',
        pageName: 'Service Detail - In-Person Support',
        defaultUrl: DEFAULT_IMAGES['/services/on-site-dedicated-services/in-person-support'],
        description: 'Hero image for On-Site Operational Support'
      }
    ]
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-4xl bg-[#181F32] border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Top Header Bar */}
          <div className="flex items-center justify-between p-5 border-b border-white/10 bg-[#111726]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F3453F]/20 text-[#F3453F] flex items-center justify-center">
                <ImageIcon size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-extrabold text-white font-heading">
                    Admin Image Management Portal
                  </h2>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <Globe size={11} className="animate-pulse" />
                    <span>Global Realtime Sync</span>
                  </span>
                </div>
                <p className="text-xs text-white/60 font-sans">
                  Paste new image URLs & update dynamically across all devices, phones & browsers
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              {isAdminAuthenticated && (
                <>
                  <button
                    onClick={handleManualSync}
                    disabled={isSyncingNow}
                    title="Force sync changes to all devices"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold border border-emerald-500/20 transition-colors"
                  >
                    <RefreshCw size={13} className={isSyncingNow ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">Sync All Devices</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs font-semibold transition-colors"
                  >
                    <LogOut size={14} />
                    <span>Logout</span>
                  </button>
                </>
              )}
              <button
                onClick={closeAdminPortal}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 hover:text-white flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body Content */}
          {!isAdminAuthenticated ? (
            /* Password Authentication Screen */
            <div className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#F3453F]/10 text-[#F3453F] border border-[#F3453F]/30 flex items-center justify-center mb-6 shadow-inner">
                <Lock size={32} />
              </div>

              <h3 className="text-2xl font-bold text-white font-heading mb-2">
                Admin Authentication Required
              </h3>
              <p className="text-sm text-white/70 max-w-md mb-8">
                Enter your administrative passcode to configure image assets for all devices.
              </p>

              <form onSubmit={handlePasswordSubmit} className="w-full max-w-sm space-y-4">
                <div>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter Passcode"
                    autoFocus
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white text-center tracking-widest text-lg focus:outline-none focus:border-[#F3453F] transition-colors"
                  />
                </div>

                {authError && (
                  <div className="flex items-center justify-center gap-2 text-xs font-semibold text-[#F3453F] bg-[#F3453F]/10 p-2.5 rounded-lg border border-[#F3453F]/20">
                    <AlertCircle size={14} />
                    <span>{authError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-sm transition-all shadow-lg shadow-[#F3453F]/20"
                >
                  Unlock Admin Portal
                </button>
              </form>
            </div>
          ) : (
            /* Admin Dashboard Screen */
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Tab Navigation */}
              <div className="flex items-center gap-1 p-2 bg-[#141A29] border-b border-white/10 overflow-x-auto no-scrollbar">
                {[
                  { id: 'branding', label: 'Branding & Logo', icon: Layout },
                  { id: 'home', label: 'Home Page', icon: Grid },
                  { id: 'founder', label: 'Founder Page', icon: User },
                  { id: 'services', label: 'Service Categories', icon: Briefcase },
                  { id: 'subservices', label: 'Detailed Subservices', icon: ExternalLink },
                  { id: 'cloud', label: 'Cloud Sync & Export', icon: Sliders }
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-[#F3453F] text-white shadow-md'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon size={14} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tab Content List */}
              <div className="flex-1 p-6 overflow-y-auto space-y-6">
                {activeTab === 'cloud' ? (
                  /* Cloud Sync Settings & Export Tab */
                  <div className="space-y-6">
                    <div className="glass-panel-white p-5 bg-[#141A29]/80 border border-white/10 rounded-xl space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-base font-bold text-white font-heading">
                            Active Live Cloud Synchronization
                          </h4>
                          <p className="text-xs text-white/70">
                            Changes made in this admin panel are pushed to the Cloud Sync Engine and broadcasted across all devices, mobile phones, and laptops in real-time.
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-full text-xs font-bold flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                          Live Connected
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                        <button
                          onClick={handleManualSync}
                          disabled={isSyncingNow}
                          className="p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center gap-3 text-left transition-colors"
                        >
                          <RefreshCw size={18} className={`text-emerald-400 ${isSyncingNow ? 'animate-spin' : ''}`} />
                          <div>
                            <span className="text-xs font-bold text-white block">Force Cloud Sync Now</span>
                            <span className="text-[10px] text-white/50">Push local state to all connected devices</span>
                          </div>
                        </button>

                        <button
                          onClick={handleCopyJson}
                          className="p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center gap-3 text-left transition-colors"
                        >
                          <Copy size={18} className="text-[#F3453F]" />
                          <div>
                            <span className="text-xs font-bold text-white block">
                              {jsonCopied ? '✅ Copied to Clipboard!' : 'Copy All Image URLs as JSON'}
                            </span>
                            <span className="text-[10px] text-white/50">Export clean configuration dictionary</span>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Optional Custom Firebase Config */}
                    <div className="glass-panel-white p-5 bg-[#141A29]/80 border border-white/10 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-white font-heading">
                            Custom Firebase Firestore Configuration (Optional)
                          </h4>
                          <p className="text-xs text-white/60">
                            By default, the global cloud sync relay handles cross-device syncing. If you wish to connect your own Firebase project, paste your Firebase config JSON below.
                          </p>
                        </div>
                      </div>

                      <textarea
                        value={customFirebaseJson}
                        onChange={(e) => setCustomFirebaseJson(e.target.value)}
                        placeholder={`{\n  "apiKey": "AIzaSy...",\n  "projectId": "my-stellars-project"\n}`}
                        rows={5}
                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white font-mono text-xs focus:outline-none focus:border-[#F3453F]"
                      />

                      <div className="flex items-center justify-between pt-1">
                        {firebaseSavedSuccess ? (
                          <span className="text-xs font-bold text-emerald-400">
                            ✅ Firebase configuration updated successfully!
                          </span>
                        ) : (
                          <span className="text-[11px] text-white/40">
                            Leave empty to use built-in global cloud relay.
                          </span>
                        )}
                        <button
                          onClick={handleSaveFirebaseConfig}
                          className="px-4 py-2 bg-[#F3453F] hover:bg-[#D93833] text-white text-xs font-bold rounded-lg transition-colors"
                        >
                          Save Config
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  imageConfig[activeTab]?.map((item) => {
                    const currentUrl = getImage(item.key, item.defaultUrl);
                    const isCustom = currentUrl !== item.defaultUrl;
                    const tempValue = tempInputs[item.key] !== undefined ? tempInputs[item.key] : currentUrl;

                    return (
                      <div
                        key={item.key}
                        className="glass-panel-white p-5 bg-[#141A29]/80 border border-white/10 rounded-xl flex flex-col md:flex-row items-start md:items-center gap-5 transition-all hover:border-white/20"
                      >
                        {/* Image Thumbnail Preview */}
                        <div className="w-full md:w-36 h-28 rounded-lg overflow-hidden bg-black/40 border border-white/15 relative shrink-0 group flex items-center justify-center">
                          <img
                            src={tempValue || currentUrl}
                            alt={item.title}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = item.defaultUrl;
                            }}
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <Eye size={18} className="text-white" />
                          </div>
                          {isCustom && (
                            <span className="absolute top-1.5 right-1.5 text-[9px] font-extrabold uppercase bg-[#F3453F] text-white px-1.5 py-0.5 rounded shadow">
                              Cloud Override
                            </span>
                          )}
                        </div>

                        {/* Input & Info Controls */}
                        <div className="flex-1 w-full space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <h4 className="text-sm font-extrabold text-white font-heading">
                                {item.title}
                              </h4>
                              <span className="text-[11px] font-semibold text-[#F3453F] block">
                                {item.pageName}
                              </span>
                            </div>
                            {isCustom && (
                              <button
                                onClick={() => {
                                  resetImage(item.key);
                                  handleTempInputChange(item.key, item.defaultUrl);
                                }}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-white/60 hover:text-white transition-colors"
                              >
                                <RotateCcw size={12} />
                                <span>Reset Default</span>
                              </button>
                            )}
                          </div>

                          <p className="text-xs text-white/70 font-sans">
                            {item.description}
                          </p>

                          <div className="flex items-center gap-2 pt-1">
                            <input
                              type="text"
                              value={tempValue}
                              onChange={(e) => handleTempInputChange(item.key, e.target.value)}
                              placeholder="Paste Image URL (https://...)"
                              className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-white text-xs font-mono focus:outline-none focus:border-[#F3453F] transition-colors"
                            />
                            <button
                              onClick={() => handleApplyImage(item.key, tempValue)}
                              className="px-4 py-2 rounded-lg bg-[#F3453F] hover:bg-[#D93833] text-white text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 shadow-sm"
                            >
                              {copiedKey === item.key ? (
                                <>
                                  <Check size={14} />
                                  <span>Synced Globally!</span>
                                </>
                              ) : (
                                <span>Update All Devices</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Bottom Footer Control Bar */}
              <div className="p-4 bg-[#111726] border-t border-white/10 flex items-center justify-between text-xs text-white/60">
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset all image overrides to system defaults across all devices?')) {
                      resetAllImages();
                      setTempInputs({});
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 font-semibold transition-colors"
                >
                  <RotateCcw size={13} />
                  <span>Reset All Images to Default</span>
                </button>

                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-medium text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span>Cross-Device Live Sync Active</span>
                  </span>
                  <button
                    onClick={closeAdminPortal}
                    className="px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AdminImageModal;
